package com.yogaaicoach.platform

import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.runtime.Composable
import js.promise.catch
import web.dom.ElementId
import web.dom.document
import web.events.EventHandler
import web.events.addHandler
import web.html.HTMLAudioElement
import web.html.HTMLElement
import web.html.HTMLMediaElement
import web.html.HTMLVideoElement
import web.html.canPlayEvent
import web.html.endedEvent

// WorkoutPage.kt renders two <video> slots and one <audio> element directly in the React tree.
// The video slots deliberately have NO React-controlled src: every src assignment goes through
// this file. That is what makes swapVideos() safe — it changes which slot is on screen, and
// React never reconciles a src back onto the element it thinks is the active one.
// PlayAzureVideo / AudioContainerNew are Composable-only and unused in the React layer.

const val WORKOUT_VIDEO_A_ID = "workout-video-a"
const val WORKOUT_VIDEO_B_ID = "workout-video-b"
const val WORKOUT_AUDIO_ID = "workout-audio"

private var isSlotAActive = true
private var endedUnsubscribe: (() -> Unit)? = null
private var activeCanPlayUnsubscribe: (() -> Unit)? = null
private var preloadCanPlayUnsubscribe: (() -> Unit)? = null

@Composable
actual fun ColumnScope.PlayAzureVideo(sasUrl: String, onReady: () -> Unit) {
    // Unused in jsMain — WorkoutPage renders the video elements directly.
}

@Composable
actual fun AudioContainerNew(url: String, onReady: () -> Unit) {
    // Unused in jsMain — WorkoutPage renders the audio element directly.
}

private fun videoSlot(id: String): HTMLVideoElement? =
    document.getElementById(ElementId(id)) as? HTMLVideoElement

private fun activeVideo(): HTMLVideoElement? =
    videoSlot(if (isSlotAActive) WORKOUT_VIDEO_A_ID else WORKOUT_VIDEO_B_ID)

private fun preloadVideo(): HTMLVideoElement? =
    videoSlot(if (isSlotAActive) WORKOUT_VIDEO_B_ID else WORKOUT_VIDEO_A_ID)

private fun audioElement(): HTMLAudioElement? =
    document.getElementById(ElementId(WORKOUT_AUDIO_ID)) as? HTMLAudioElement

/**
 * Points the active slot at [url] and pre-warms it: playback is held at frame 0 until
 * [onReady] fires and the caller starts the exercise with [continueVideo].
 *
 * Called once per exercise for the first part (intro, or the loop when there is no intro).
 * Every later part of the same exercise arrives through [loadPreloadVideo] + [swapVideos].
 */
fun loadWorkoutVideo(url: String, onReady: (HTMLVideoElement) -> Unit) {
    setVideoEndedListener(null)
    cancelPreloadCallback()
    activeCanPlayUnsubscribe?.invoke()
    activeCanPlayUnsubscribe = null

    val video = activeVideo() ?: return
    video.playbackRate = 1.0
    var unsubscribe: (() -> Unit)? = null
    unsubscribe = video.canPlayEvent.addHandler(EventHandler {
        unsubscribe?.invoke()
        activeCanPlayUnsubscribe = null
        video.pause() // pre-warm decoder: frame 0 is decoded and held ready
        onReady(video)
    })
    activeCanPlayUnsubscribe = unsubscribe
    video.src = url
    video.load()
}

/**
 * Puts slot A back on screen and drops every pending listener. Which slot is active is module
 * state that outlives the React elements, so a remount has to start from a known slot — otherwise
 * the active slot would be the one the stylesheet still hides.
 */
fun resetWorkoutVideoSlots() {
    setVideoEndedListener(null)
    cancelPreloadCallback()
    activeCanPlayUnsubscribe?.invoke()
    activeCanPlayUnsubscribe = null
    isSlotAActive = true
    // Dropping the inline display set by swapVideos() hands visibility back to the classnames
    videoSlot(WORKOUT_VIDEO_A_ID)?.style?.removeProperty("display")
    videoSlot(WORKOUT_VIDEO_B_ID)?.style?.removeProperty("display")
}

/**
 * Resets the slots and stops any part still downloading — for when the workout screen goes away.
 */
fun releaseWorkoutVideos() {
    resetWorkoutVideoSlots()
    listOf(videoSlot(WORKOUT_VIDEO_A_ID), videoSlot(WORKOUT_VIDEO_B_ID)).forEach { video ->
        video?.pause()
        video?.removeAttribute("src")
        video?.load()
    }
}

// play() rejects whenever a pause lands while the request is still in flight, which is routine
// during part swaps: https://developer.chrome.com/blog/play-request-was-interrupted
private fun HTMLMediaElement.playSafely() {
    playAsync().catch { null }
}

actual fun pauseVideo() { activeVideo()?.pause() }
actual fun pauseAudio() { audioElement()?.pause() }
actual fun continueVideo() { activeVideo()?.playSafely() }
actual fun continueAudio() { audioElement()?.playSafely() }

// Hide the container rather than a single slot — both video elements have to go away when a
// Compose overlay appears, since native DOM nodes always render above the Compose canvas.
private fun videoContainer(): HTMLElement? =
    document.getElementById(ElementId("workout-video-container")) as? HTMLElement

actual fun hideVideo() {
    activeVideo()?.pause()
    videoContainer()?.style?.setProperty("visibility", "hidden")
}

actual fun showVideo() {
    videoContainer()?.style?.removeProperty("visibility")
}

actual fun setVideoToSecond(second: Int) {
    activeVideo()?.let {
        it.currentTime = second.toDouble()
        // The loop cycle jump arrives on the `ended` event, which leaves the element paused
        it.playSafely()
    }
}

actual fun setAudioToSecond(second: Int) {
    audioElement()?.let {
        it.currentTime = second.toDouble()
        it.playSafely()
    }
}

actual fun setVideoSpeed(speed: Float) {
    activeVideo()?.let { it.playbackRate = speed.toDouble() }
}

actual fun setAudioSpeed(speed: Float) {
    audioElement()?.let { it.playbackRate = speed.toDouble() }
}

actual fun loadPreloadVideo(url: String, onReady: () -> Unit) {
    val preload = preloadVideo() ?: return
    cancelPreloadCallback()

    preload.playbackRate = 1.0
    var unsubscribe: (() -> Unit)? = null
    unsubscribe = preload.canPlayEvent.addHandler(EventHandler {
        unsubscribe?.invoke()
        preloadCanPlayUnsubscribe = null
        preload.pause() // hold at frame 0 so the swap has a decoded frame to show instantly
        onReady()
    })
    preloadCanPlayUnsubscribe = unsubscribe
    preload.src = url
    preload.load()
}

/**
 * Shows the preloaded slot and hides the active one. Nothing is re-fetched and no decoder is
 * reset — both elements stay at their buffered positions — so the part transition has no flash.
 */
actual fun swapVideos() {
    val active = activeVideo() ?: return
    val preload = preloadVideo() ?: return

    preload.style.setProperty("display", "block")
    active.style.setProperty("display", "none")
    active.pause()

    isSlotAActive = !isSlotAActive
    preload.playSafely()
}

actual fun setVideoEndedListener(listener: (() -> Unit)?) {
    endedUnsubscribe?.invoke()
    endedUnsubscribe = null
    if (listener == null) return
    val video = activeVideo() ?: return
    endedUnsubscribe = video.endedEvent.addHandler(EventHandler { listener() })
}

private fun cancelPreloadCallback() {
    preloadCanPlayUnsubscribe?.invoke()
    preloadCanPlayUnsubscribe = null
}
